import React, { useMemo } from 'react';
import { Box, Grid, Paper, Typography, Chip, Alert, Snackbar, Button } from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import { PageHeader, SkeletonCards, ErrorState, PermissionState, UsageMeter, ConfirmDialog } from '../ui';
import PlanCard from '../GlobalAdmin/plans/PlanCard';
import PlanCompareMatrix from '../GlobalAdmin/plans/PlanCompareMatrix';
import { formatPrice } from '../GlobalAdmin/plans/planUtils';
import useSubscriptionPage, { changeModeFor } from './useSubscriptionPage';

// Feature metadata isn't served to tenants (only GA gets /ga/features), so
// the tenant page derives names from the plan entitlements it can see plus
// this small display map. Keys mirror domain/subscription/features.py.
const FEATURE_META = {
  'products.max': { name: 'Products', category: 'Catalog', kind: 'limit' },
  categories: { name: 'Categories', category: 'Catalog', kind: 'boolean' },
  inventory: { name: 'Inventory tracking', category: 'Catalog', kind: 'boolean' },
  'products.export': { name: 'Product export', category: 'Catalog', kind: 'boolean' },
  'users.seats': { name: 'Team seats', category: 'Team', kind: 'limit' },
  'users.admin_only_seats': { name: 'Admin-only seats', category: 'Team', kind: 'boolean' },
  'orders.monthly': { name: 'Orders received', category: 'Sales', kind: 'metered' },
  invoices: { name: 'Invoices', category: 'Sales', kind: 'boolean' },
  'invoices.issued.monthly': { name: 'Invoices issued', category: 'Sales', kind: 'metered' },
  'invoices.pdf': { name: 'Invoice PDFs & shipping labels', category: 'Sales', kind: 'boolean' },
  'coupons.active.max': { name: 'Active coupons', category: 'Marketing', kind: 'limit' },
  advertisements: { name: 'Advertisements', category: 'Marketing', kind: 'boolean' },
  'advertisements.active.max': { name: 'Active campaigns', category: 'Marketing', kind: 'limit' },
  reporting: { name: 'Reporting', category: 'Insights', kind: 'boolean' },
  'reports.generated.monthly': { name: 'Reports generated', category: 'Insights', kind: 'metered' },
  'branding.logo': { name: 'Custom logo', category: 'Company', kind: 'boolean' },
  multi_currency: { name: 'Multi-currency', category: 'Company', kind: 'boolean' },
};
const TOP_FEATURE_KEYS = ['products.max', 'users.seats', 'invoices.issued.monthly', 'reporting'];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '');

const Subscription = () => {
  const p = useSubscriptionPage();
  const { t } = p;
  const features = useMemo(() => Object.entries(FEATURE_META).map(([key, m]) => ({ key, ...m, name: t(m.name, { defaultValue: m.name }) })), [t]);
  const featuresByKey = useMemo(() => Object.fromEntries(features.map((f) => [f.key, f])), [features]);

  if (p.status === 'loading') return <SkeletonCards rows={4} />;
  if (p.status === 'error') {
    return <ErrorState title={p.t("Couldn't load your subscription", { defaultValue: "Couldn't load your subscription" })} request={p.errorInfo?.request} retryLabel={p.t('Try again', { defaultValue: 'Try again' })} onRetry={p.load} />;
  }
  if (p.status === 'permission') return <PermissionState title={p.t("You don't have access to Subscription", { defaultValue: "You don't have access to Subscription" })} />;

  const { subscription, subscribed, managed, isStripe, pastDue, canAct, billing } = p;
  // Shown only when Stripe Tax is collecting: prices are tax-exclusive and
  // the exact GST/HST/PST depends on the company's billing address.
  const taxNote = billing.taxEnabled ? p.t('plus applicable tax', { defaultValue: 'plus applicable tax' }) : undefined;
  const validUntil = subscription?.validUntil ? new Date(subscription.validUntil) : null;
  const periodEnd = subscription?.currentPeriodEnd || p.entitlements?.periodEnd || null;
  const pendingPlan = subscription?.pendingPlan || null;
  const cancelAtPeriodEnd = Boolean(subscription?.cancelAtPeriodEnd);
  const topFeaturesFor = (plan) => TOP_FEATURE_KEYS.map((key) => ({ feature: featuresByKey[key], entitlement: plan.entitlements.find((e) => e.featureKey === key) }));

  const selectLabelFor = (plan) => {
    const mode = changeModeFor(plan, p.currentPlan, isStripe);
    if (mode === 'upgrade') return p.t('Upgrade to {{plan}}', { plan: plan.name, defaultValue: `Upgrade to ${plan.name}` });
    if (mode === 'downgrade') return p.t('Downgrade to {{plan}}', { plan: plan.name, defaultValue: `Downgrade to ${plan.name}` });
    return p.t('Subscribe to {{plan}}', { plan: plan.name, defaultValue: `Subscribe to ${plan.name}` });
  };

  const subtitle = managed
    ? p.t('Your company is on {{plan}}', { plan: p.currentPlan?.name, defaultValue: `Your company is on ${p.currentPlan?.name}` })
    : subscribed
      ? p.t('Your company is on {{plan}}', { plan: p.currentPlan?.name, defaultValue: `Your company is on ${p.currentPlan?.name}` })
      : p.t('Choose a plan to get started', { defaultValue: 'Choose a plan to get started' });

  const confirmCopy = () => {
    if (!p.confirm) return { title: '', description: '', confirmLabel: '' };
    const { plan, mode } = p.confirm;
    if (mode === 'checkout') {
      return {
        title: p.t('Subscribe to {{plan}}?', { plan: plan.name, defaultValue: `Subscribe to ${plan.name}?` }),
        description: p.t("You'll be taken to our secure Stripe checkout. Your plan starts the moment payment completes and renews every month on the same date.", { defaultValue: "You'll be taken to our secure Stripe checkout. Your plan starts the moment payment completes and renews every month on the same date." }),
        confirmLabel: p.t('Continue to checkout', { defaultValue: 'Continue to checkout' }),
      };
    }
    if (mode === 'upgrade') {
      return {
        title: p.t('Upgrade to {{plan}}?', { plan: plan.name, defaultValue: `Upgrade to ${plan.name}?` }),
        description: p.t("Starts now. You'll be charged a prorated amount for the rest of this billing period, and your renewal date stays {{date}}.", { date: fmtDate(periodEnd), defaultValue: `Starts now. You'll be charged a prorated amount for the rest of this billing period, and your renewal date stays ${fmtDate(periodEnd)}.` }),
        confirmLabel: p.t('Upgrade now', { defaultValue: 'Upgrade now' }),
      };
    }
    return {
      title: p.t('Downgrade to {{plan}}?', { plan: plan.name, defaultValue: `Downgrade to ${plan.name}?` }),
      description: p.overage
        ? p.t('Your company is above some limits of this plan. Nothing is deleted, but once it takes effect you cannot add more until you are under the limit.', { defaultValue: 'Your company is above some limits of this plan. Nothing is deleted, but once it takes effect you cannot add more until you are under the limit.' })
        : p.t('Takes effect on {{date}}, at the end of your current billing period. You keep {{current}} until then and nothing is refunded.', { date: fmtDate(periodEnd), current: p.currentPlan?.name, defaultValue: `Takes effect on ${fmtDate(periodEnd)}, at the end of your current billing period. You keep ${p.currentPlan?.name} until then and nothing is refunded.` }),
      confirmLabel: p.overage ? p.t('Schedule anyway', { defaultValue: 'Schedule anyway' }) : p.t('Schedule downgrade', { defaultValue: 'Schedule downgrade' }),
    };
  };
  const copy = confirmCopy();

  return (
    <Box>
      <PageHeader
        title={p.t('Subscription', { defaultValue: 'Subscription' })}
        subtitle={subtitle}
        helpKey="subscription"
        actions={isStripe && p.isAdmin ? [{
          label: p.t('Manage billing', { defaultValue: 'Manage billing' }), icon: CreditCardOutlinedIcon, variant: 'outlined', onClick: p.openPortal, disabled: p.busy,
        }] : []}
      />

      {pastDue && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          data-testid="past-due-alert"
          action={p.isAdmin ? <Button color="inherit" size="small" onClick={p.openPortal}>{p.t('Update payment method', { defaultValue: 'Update payment method' })}</Button> : undefined}
        >
          {p.t('Your last payment failed. Update your payment method by {{date}} to keep {{plan}}; after that your company loses access until it pays.', { date: fmtDate(subscription?.graceUntil), plan: p.currentPlan?.name, defaultValue: `Your last payment failed. Update your payment method by ${fmtDate(subscription?.graceUntil)} to keep ${p.currentPlan?.name}; after that your company loses access until it pays.` })}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }} data-testid="current-plan">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h6">{p.currentPlan?.name || p.t('No plan', { defaultValue: 'No plan' })}</Typography>
          {p.currentPlan && managed && (
            <Chip color="secondary" icon={<HandshakeOutlinedIcon />} label={p.t('Managed partnership', { defaultValue: 'Managed partnership' })} data-testid="managed-chip" />
          )}
          {p.currentPlan && !managed && (
            <Chip color="primary" label={`${formatPrice(p.currentPlan.price?.amount, p.currentPlan.price?.currency)} / ${p.t('month', { defaultValue: 'month' })}${taxNote ? ` · ${taxNote}` : ''}`} />
          )}
          {pastDue && <Chip color="error" size="small" label={p.t('Payment failed', { defaultValue: 'Payment failed' })} />}
          {managed && validUntil && (
            <Typography variant="caption" color="text.secondary">
              {p.t('Valid until {{date}}', { date: validUntil.toLocaleDateString(), defaultValue: `Valid until ${validUntil.toLocaleDateString()}` })}
            </Typography>
          )}
          {subscribed && !managed && periodEnd && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }} data-testid="period-line">
              <EventRepeatOutlinedIcon fontSize="inherit" />
              {cancelAtPeriodEnd
                ? p.t('Ends {{date}}', { date: fmtDate(periodEnd), defaultValue: `Ends ${fmtDate(periodEnd)}` })
                : p.t('Renews {{date}} · limits reset then', { date: fmtDate(periodEnd), defaultValue: `Renews ${fmtDate(periodEnd)} · limits reset then` })}
            </Typography>
          )}
        </Box>
        {p.meters.length > 0 ? (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {p.meters.map((m) => (
              <Grid item xs={12} sm={6} md={4} key={m.key}>
                <UsageMeter label={featuresByKey[m.key]?.name || m.key} usage={m} unlimitedLabel={p.t('unlimited', { defaultValue: 'unlimited' })} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {subscribed ? p.t('No usage to show yet.', { defaultValue: 'No usage to show yet.' }) : p.t('Creating products, invoices and orders is paused until your company subscribes. Nothing has been deleted.', { defaultValue: 'Creating products, invoices and orders is paused until your company subscribes. Nothing has been deleted.' })}
          </Typography>
        )}
        {isStripe && p.isAdmin && (
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
            {cancelAtPeriodEnd ? (
              <Button size="small" variant="outlined" onClick={p.resumePlan} disabled={p.busy}>{p.t('Resume subscription', { defaultValue: 'Resume subscription' })}</Button>
            ) : (
              <Button size="small" variant="text" color="inherit" onClick={() => p.setCancelOpen(true)} disabled={p.busy}>{p.t('Cancel subscription', { defaultValue: 'Cancel subscription' })}</Button>
            )}
          </Box>
        )}
      </Paper>

      {pendingPlan && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          data-testid="pending-alert"
          action={p.isAdmin ? <Button color="inherit" size="small" onClick={p.keepCurrentPlan} disabled={p.busy}>{p.t('Keep {{plan}}', { plan: p.currentPlan?.name, defaultValue: `Keep ${p.currentPlan?.name}` })}</Button> : undefined}
        >
          {p.t('Your company moves to {{plan}} on {{date}}.', { plan: pendingPlan.name || pendingPlan.slug, date: fmtDate(periodEnd), defaultValue: `Your company moves to ${pendingPlan.name || pendingPlan.slug} on ${fmtDate(periodEnd)}.` })}
        </Alert>
      )}
      {cancelAtPeriodEnd && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="cancel-alert">
          {p.t('Your subscription ends on {{date}}. After that, creating new records is paused until you subscribe again; your data stays.', { date: fmtDate(periodEnd), defaultValue: `Your subscription ends on ${fmtDate(periodEnd)}. After that, creating new records is paused until you subscribe again; your data stays.` })}
        </Alert>
      )}

      {managed ? (
        <Alert severity="info" icon={<HandshakeOutlinedIcon fontSize="inherit" />} sx={{ mb: 2 }} data-testid="managed-alert">
          {p.t('Your plan is managed by the SmartInventory team under your partnership agreement. Every feature is included; to change anything about it, contact us.', { defaultValue: 'Your plan is managed by the SmartInventory team under your partnership agreement. Every feature is included; to change anything about it, contact us.' })}
        </Alert>
      ) : (
        <>
          {!subscribed && (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="unsubscribed-alert">
              {p.t('Your company has no active subscription. Pick a plan below — it starts the moment payment completes.', { defaultValue: 'Your company has no active subscription. Pick a plan below — it starts the moment payment completes.' })}
            </Alert>
          )}
          {!p.isAdmin && (
            <Alert severity="info" sx={{ mb: 2 }}>{p.t('Only a company admin can change the plan.', { defaultValue: 'Only a company admin can change the plan.' })}</Alert>
          )}
          {p.isAdmin && !billing.stripeEnabled && (
            <Alert severity="info" sx={{ mb: 2 }} data-testid="billing-unavailable-alert">{p.t('Online payments are not available yet — contact us to subscribe or change your plan.', { defaultValue: 'Online payments are not available yet — contact us to subscribe or change your plan.' })}</Alert>
          )}

          <Typography variant="subtitle1" sx={{ mb: 1 }}>{p.t('All plans', { defaultValue: 'All plans' })}</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }} alignItems="stretch" data-tour="subscription-plans">
            {p.plans.map((plan) => (
              <Grid item xs={12} sm={6} lg={3} key={plan._id}>
                <PlanCard
                  plan={plan}
                  mode="tenant"
                  t={p.t}
                  topFeatures={topFeaturesFor(plan)}
                  isCurrent={subscribed && plan._id === p.currentPlan?._id}
                  selectDisabled={!canAct || pastDue || p.busy || (pendingPlan && pendingPlan._id === plan._id)}
                  selectLabel={selectLabelFor(plan)}
                  onSelect={() => p.startSwitch(plan)}
                  taxNote={taxNote}
                />
              </Grid>
            ))}
          </Grid>

          <Typography variant="subtitle1" sx={{ mb: 1 }}>{p.t('Compare plans', { defaultValue: 'Compare plans' })}</Typography>
          <PlanCompareMatrix features={features} plans={p.plans} t={p.t} linkToBuilder={false} />
        </>
      )}

      <ConfirmDialog
        open={Boolean(p.confirm)}
        onClose={() => !p.busy && p.setConfirm(null)}
        tone="primary"
        icon={SwapHorizIcon}
        title={copy.title}
        description={copy.description}
        records={p.overage ? p.overage.map((o) => `${featuresByKey[o.featureKey]?.name || o.featureKey}: ${o.used} / ${o.limit}`) : undefined}
        cancelLabel={p.t('ga.common.cancel')}
        confirmLabel={copy.confirmLabel}
        onConfirm={() => p.performSwitch(Boolean(p.overage))}
        loading={p.busy}
      />

      <ConfirmDialog
        open={p.cancelOpen}
        onClose={() => !p.busy && p.setCancelOpen(false)}
        tone="danger"
        title={p.t('Cancel your subscription?', { defaultValue: 'Cancel your subscription?' })}
        description={p.t('You keep {{plan}} until {{date}}. After that, creating new records is paused until you subscribe again. Your data is never deleted, and you can resume any time before then.', { plan: p.currentPlan?.name, date: fmtDate(periodEnd), defaultValue: `You keep ${p.currentPlan?.name} until ${fmtDate(periodEnd)}. After that, creating new records is paused until you subscribe again. Your data is never deleted, and you can resume any time before then.` })}
        cancelLabel={p.t('Keep my plan', { defaultValue: 'Keep my plan' })}
        confirmLabel={p.t('Cancel at period end', { defaultValue: 'Cancel at period end' })}
        onConfirm={p.cancelPlan}
        loading={p.busy}
      />

      <Snackbar open={p.snackbar.open} autoHideDuration={5000} onClose={() => p.setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={p.snackbar.severity} sx={{ width: '100%' }}>{p.snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Subscription;
